import {
  Component,
  input,
  output,
  ViewChild,
  forwardRef,
} from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { IonInput } from '@ionic/angular/standalone';
import { ButtonComponent } from 'src/app/ui-kit/button/button.component';
import { IconComponent } from 'src/app/ui-kit/icon/icon.component';
/**
 * Page Title Search Input Component
 *
 * A simple search input component designed for page titles.
 * Implements Angular's ControlValueAccessor for seamless form integration.
 *
 * Features:
 * - Angular Forms integration (Reactive and Template-driven)
 * - Programmatic focus/blur control
 * - Search-optimized defaults
 * - Simple, clean API
 *
 * @example
 * // Template-driven form
 * <app-page-title-search-input
 *   placeholder="Search partners..."
 *   [(ngModel)]="searchQuery"
 * ></app-page-title-search-input>
 *
 * @example
 * // Reactive form with programmatic focus
 * <app-page-title-search-input
 *   [formControl]="searchControl"
 *   (ionInput)="onSearch($event)"
 * ></app-page-title-search-input>
 *
 * @ViewChild(PageTitleSearchInputComponent) searchInput!: PageTitleSearchInputComponent;
 * this.searchInput.setFocus();
 */
@Component({
  selector: 'app-page-title-search-input',
  templateUrl: './page-title-search-input.component.html',
  styleUrls: ['./page-title-search-input.component.scss'],
  standalone: true,
  imports: [IonInput, ButtonComponent, IconComponent],
  host: {
    display: 'block',
  },
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PageTitleSearchInputComponent),
      multi: true,
    },
  ],
})
export class PageTitleSearchInputComponent implements ControlValueAccessor {
  /**
   * Reference to the underlying Ionic input element
   */
  @ViewChild(IonInput, { static: false }) inputElement!: IonInput;

  // ========================================
  // Basic Properties
  // ========================================

  /**
   * Placeholder text shown when input is empty
   * @default "Search..."
   */
  readonly placeholder = input<string>('Search...');

  /**
   * Disables the input
   * @default false
   */
  disabled: boolean = false;

  /**
   * Debounce time in milliseconds for ionInput event
   * Useful for reducing rapid search calls
   * @default 300
   */
  readonly debounce = input<number>(300);

  /**
   * Automatically focuses the input when component loads
   * @default false
   */
  readonly autofocus = input<boolean>(false);

  /**
   * Shows a clear button when input has a value
   * @default true
   */
  readonly clearInput = input<boolean>(true);

  /**
   * Ionic color palette
   * @default "medium"
   */
  readonly color = input<string>('medium');

  /**
   * Shape of the input
   * @default "round"
   */
  readonly shape = input<'round'>('round');

  /**
   * Fill style
   * @default "solid"
   */
  readonly fill = input<'outline' | 'solid'>('solid');

  /**
   * The mode determines which platform styles to use
   * @default "ios"
   */
  readonly mode = input<'ios' | 'md'>('ios');

  /**
   * Whether to include a filter button
   * @default true
   */
  readonly filterButton = input<boolean>(true);

  /**
   * Whether the filter is active
   * @default false
   */
  readonly filterActive = input<boolean>(false);

  /**
   * Color of the filter button
   * @default "dark"
   */
  readonly filterButtonColor = input<string>('dark');

  /**
   * Whether to show a badge on the filter button
   * @default false
   */
  readonly filterBadge = input<boolean>(false);

  // ========================================
  // Events
  // ========================================

  /**
   * Emitted when the input value changes
   */
  readonly onInput = output<any>();

  /**
   * Emitted when the input value changes
   */
  readonly ionChange = output<any>();

  /**
   * Emitted when the input gains focus
   */
  readonly onFocus = output<any>();

  /**
   * Emitted when the input loses focus
   */
  readonly onBlur = output<any>();

  /**
   * Emitted when the Enter key is pressed
   */
  readonly enterPressed = output<void>();

  /**
   * Emitted when the filter is clicked
   */
  readonly onFilterClick = output<Event>();

  // ========================================
  // ControlValueAccessor Properties
  // ========================================

  /**
   * Internal value of the input
   */
  value: string | null = null;

  /**
   * Callback function to notify Angular forms of value changes
   */
  private onChange: (value: string | null) => void = () => {};

  /**
   * Callback function to notify Angular forms when input is touched
   */
  private onTouched: () => void = () => {};

  // ========================================
  // Lifecycle Hooks
  // ========================================

  ngAfterViewInit(): void {
    if (this.autofocus()) {
      setTimeout(() => this.setFocus(), 100);
    }
  }

  // ========================================
  // ControlValueAccessor Implementation
  // ========================================

  /**
   * Writes a new value to the component
   * @param value - The new value to set
   */
  writeValue(value: string | null): void {
    this.value = value;
  }

  /**
   * Registers a callback function for value changes
   * @param fn - The callback function
   */
  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  /**
   * Registers a callback function for touch events
   * @param fn - The callback function
   */
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  /**
   * Enables or disables the input
   * @param isDisabled - Whether the input should be disabled
   */
  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // ========================================
  // Event Handlers
  // ========================================

  /**
   * Handles the ionInput event
   * @param event - The Ionic input event
   */
  onInputChange(event: { detail: { value?: string | null } }): void {
    const value = event.detail.value ?? '';
    this.value = value;
    this.onChange(value);
    this.onInput.emit(event);
  }

  /**
   * Handles the ionChange event
   * @param event - The Ionic change event
   */
  onIonChange(event: CustomEvent): void {
    this.ionChange.emit(event);
  }
  
  /**
   * Handles the ionFocus event
   * @param event - The Ionic focus event
   */
  onFocusChange(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.onFocus.emit(event);
  }

  /**
   * Handles the ionBlur event
   * @param event - The Ionic blur event
   */
  onBlurChange(event: Event): void {
    this.onTouched();
    this.onBlur.emit(event);
  }

  /**
   * Handles the Enter key press event
   * @param event - The keyboard event
   */
  onEnterPressed(event: Event): void {
    this.enterPressed.emit();
    this.setBlur();
    event.preventDefault();
    event.stopPropagation();
  }

  // ========================================
  // Public Methods
  // ========================================

  /**
   * Sets focus on the native input element
   *
   * @example
   * @ViewChild(PageTitleSearchInputComponent) searchInput!: PageTitleSearchInputComponent;
   * this.searchInput.setFocus();
   */
  public async setFocus(): Promise<void> {
    if (this.inputElement) {
      await this.inputElement.setFocus();
    }
  }

  /**
   * Removes focus from the input element
   *
   * @example
   * this.searchInput.setBlur();
   */
  public async setBlur(): Promise<void> {
    if (this.inputElement) {
      await this.inputElement.getInputElement().then((inputElement) => {
        inputElement.blur();
      });
    }
  }

  /**
   * Gets the native HTML input element
   * @returns Promise that resolves to the native HTMLInputElement
   */
  async getInputElement(): Promise<HTMLInputElement> {
    if (this.inputElement) {
      return await this.inputElement.getInputElement();
    }
    throw new Error('Ion-input not initialized');
  }

  /**
   * Handles the filter click event
   */
  filterClicked($event: Event): void {
    $event.preventDefault();
    $event.stopPropagation();
    this.onFilterClick.emit($event);
  }
}
